import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  type SelectGroupLabelProps as AriakitSelectGroupLabelProps,
  type SelectItemCheckProps as AriakitSelectItemCheckProps,
  type SelectItemProps as AriakitSelectItemProps,
  type SelectLabelProps as AriakitSelectLabelProps,
  type SelectPopoverProps as AriakitSelectPopoverProps,
  type SelectProps as AriakitSelectProps,
  type SelectProviderProps as AriakitSelectProviderProps,
  type SelectStore as AriakitSelectStore,
  type SelectStoreState as AriakitSelectStoreState,
  Combobox as AriakitCombobox,
  ComboboxItem as AriakitComboboxItem,
  ComboboxList as AriakitComboboxList,
  ComboboxProvider as AriakitComboboxProvider,
  PopoverArrow as AriakitPopoverArrow,
  Select as AriakitSelect,
  SelectArrow as AriakitSelectArrow,
  SelectGroup as AriakitSelectGroup,
  SelectGroupLabel as AriakitSelectGroupLabel,
  SelectItem as AriakitSelectItem,
  SelectItemCheck as AriakitSelectItemCheck,
  SelectLabel as AriakitSelectLabel,
  SelectPopover as AriakitSelectPopover,
  SelectProvider as AriakitSelectProvider,
  useSelectContext as useAriakitSelectContext,
  useSelectStore as useAriakitSelectStore,
} from '@ariakit/react';
import { isEqual } from 'lodash';
import { matchSorter } from 'match-sorter';

import { themeVars } from '../../theme';
import { Badge } from '../Badge/Badge';
import { Box } from '../Box/Box';
import { Button } from '../Button/Button';
import { ButtonIcon } from '../ButtonIcon/ButtonIcon';
import {
  IconSolidCheck,
  IconSolidCheckCircle,
  IconSolidLoading,
  IconSolidSelector,
  IconSolidX,
  IconSolidXCircle,
} from '../icons';
import { Stack } from '../Stack/Stack';
import { Text } from '../Text/Text';

import { loadingIcon } from '../../css/loadingIcon.css';
import { checkbox, checkmark, combobox, item, loadingIconPosition, popover, select } from './styles.css';

export type SelectOption = {
  name: string;
  value: string | number;
  [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
};

// Accept strings or options. Strings will be converted to options.
type InitialOptions = string[] | SelectOption[];

type SingleValue = string | number | SelectOption | undefined;

// Potential future refactoring: improve types and allow any shape of options,
// including string(s). Also, make default SelectOption for callbacks.
//
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SelectProviderProps<T = any> = {
  checkType?: 'checkmark' | 'checkbox';
  clearable?: boolean;
  defaultValue?: T;
  displayMode?: 'normal' | 'tags';
  loading?: boolean;
  options?: InitialOptions;
  value?: T;
  setOptions?: (options: T) => void;
  setValue?: (value: T) => void;
  onValueChange?: (value: T) => void;
  onSearchValueChange?: (value: string) => void;
};

const SelectContext = createContext<SelectProviderProps>({
  checkType: 'checkmark',
  displayMode: 'normal',
  loading: false,
});

const useSelectContext = () => {
  const context = useContext(SelectContext);
  if (!context) {
    throw new Error('Select components must be used within a SelectProvider');
  }
  return context;
};

const SelectProvider = <T,>({
  children,
  options,
  value: valueProp,
  onValueChange,
  ...props
}: React.PropsWithChildren<Omit<SelectProviderProps<T>, 'setValue'>>) => {
  const opts = (options ?? []).map(singleValueToOption);
  const [value, setValue] = useState(valueProp);

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const handleSetValue = (newValue: string | string[]) => {
    const noValue = Array.isArray(value) ? value.length === 0 : !value;
    // Avoid triggering the callback if we haven't initialized yet.
    const noCallback = (!opts.length && noValue) || props.loading;

    let newInternalValue: any;
    if (opts.length) {
      if (Array.isArray(newValue) && Array.isArray(value)) {
        newInternalValue = [...value];
        newValue.forEach((option) => {
          const foundOption = opts.find((o) => optionsMatch(o, option));
          const isSelected = newInternalValue.some((v: any) => optionsMatch(v, foundOption));

          if (foundOption && !isSelected) {
            newInternalValue.push(foundOption);
          }
        });

        newInternalValue = newInternalValue.filter((option: SelectOption) =>
          newValue.some((v) => optionsMatch(v, option)),
        );
      } else {
        newInternalValue = opts.find((option) => optionsMatch(option, newValue as string));
      }
    } else {
      newInternalValue = Array.isArray(newValue)
        ? newValue.map(stringOrNumberToOption)
        : stringOrNumberToOption(newValue);
    }

    setValue(newInternalValue);

    if (onValueChange && !noCallback) {
      onValueChange(newInternalValue);
    }
  };
  /* eslint-enable @typescript-eslint/no-explicit-any */

  return (
    <SelectContext.Provider
      value={{
        options: opts,
        value,
        setValue: handleSetValue,
        ...props,
      }}
    >
      {children}
    </SelectContext.Provider>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SelectProps<T = any> = Omit<AriakitSelectProps, 'defaultValue' | 'value'> & {
  checkType?: SelectProviderProps['checkType'];
  clearable?: boolean;
  creatable?: boolean;
  defaultValue?: T;
  disabled?: boolean;
  displayMode?: SelectProviderProps['displayMode'];
  filterable?: boolean;
  customFilterable?: boolean;
  loading?: SelectProviderProps['loading'];
  resultsLoading?: boolean;
  trigger?: React.ComponentType;
  options?: SelectProviderProps['options'];
  placeholder?: string;
  store?: AriakitSelectProviderProps['store'];
  value?: T;
  renderValue?: (value: AriakitSelectStoreState['value']) => React.ReactElement | string | null;
  renderTag?: (value: string) => string;
  onValueChange?: SelectProviderProps['onValueChange'];
  onSearchValueChange?: SelectProviderProps['onSearchValueChange'];
  onCreate?: (newOptionValue: string) => void;
};

const SelectInternal = <T,>({
  checkType,
  children,
  clearable,
  creatable,
  displayMode,
  filterable,
  customFilterable,
  loading,
  resultsLoading,
  store,
  value: valueProp,
  options: optionsProp,
  onValueChange,
  onSearchValueChange,
  onCreate,
  ...props
}: SelectProps<T>): React.ReactElement => {
  const [searchValue, setSearchValueImpl] = useState('');
  const setSearchValue = (newValue: string) => {
    setSearchValueImpl(newValue);
    onSearchValueChange && onSearchValueChange(newValue);
  };
  const value = valueProp ?? props.defaultValue;
  const [options, setOptions] = useState(valueToOptions(optionsProp) as SelectOption[]);
  creatable = creatable || !!onCreate;

  const defaultStore = useAriakitSelectStore({
    defaultValue: props.defaultValue ? anyOptionsToStringValue(props.defaultValue) : undefined,
  });
  store = store ?? defaultStore;

  const providerProps = {
    checkType,
    clearable,
    defaultValue: props.defaultValue ? (valueToOptions(props.defaultValue) as SelectOption[]) : undefined,
    displayMode,
    loading,
    options,
    value: valueToOptions(value),
    onValueChange,
    setOptions: (opts: SelectOption | SelectOption[]) => setOptions(Array.isArray(opts) ? opts : [opts]),
  } as const;

  const setStoreInternally = async (newOptions: string | string[]) => {
    store?.setValue(newOptions);
  };

  const handleCreateOption = (newOptionValue: string) => {
    setOptions([...options, { name: newOptionValue, value: newOptionValue }]);

    const storeValue = store?.getState().value;
    const isMulti = Array.isArray(storeValue);

    // Wrapping in a timeout as a hack to wait until setOptions has finished
    // updating the options before updating the store value. Need to do this
    // because of how handleSetValue callback works.
    setTimeout(async () => {
      if (isMulti) {
        await setStoreInternally([...storeValue, newOptionValue]);
      } else {
        await setStoreInternally(newOptionValue);
      }
    });

    if (onCreate) {
      onCreate(newOptionValue);
    }
  };

  useEffect(() => {
    if (store && valueProp) {
      const storeValue = store.getState().value;
      const stringValue = anyOptionsToStringValue(valueProp);

      if (!isEqual(stringValue, storeValue)) {
        setStoreInternally(stringValue);
      }
    }
  }, [valueProp]);

  useEffect(() => {
    if (store && optionsProp) {
      const { value: storeValue } = store.getState();
      let newOptions = valueToOptions(optionsProp) as SelectOption[];

      if (Array.isArray(newOptions)) {
        if (Array.isArray(storeValue)) {
          const missingOptions = storeValue
            .filter((v) => !newOptions.some((option) => optionsMatch(option, v)))
            .map((v) => ({ name: v, value: v }));

          const valueAsOptions = valueToOptions(value);
          const valueToOption = valueAsOptions?.reduce(
            (acc: Record<string, SelectOption>, curr: SelectOption) => {
              acc[curr.value] = curr;
              return acc;
            },
            {} as Record<string, SelectOption>,
          );

          if (missingOptions.length) {
            newOptions = [...newOptions, ...missingOptions.map((o) => valueToOption[o.value] ?? o)];
          }
        } else if (!!storeValue && !newOptions.some((option) => optionsMatch(option, storeValue))) {
          newOptions = [...newOptions, { name: storeValue, value: storeValue }];
        }

        setOptions(newOptions);
      }
    }
  }, [optionsProp]);

  const matches = useMemo(
    () => (filterable ? matchSorter(options, searchValue, { keys: ['name', 'value'] }) : options),
    [options, searchValue],
  );

  const hasExactMatch = useMemo(
    () =>
      filterable || customFilterable
        ? matchSorter(matches, searchValue, {
            keys: ['name', 'value'],
            threshold: matchSorter.rankings.CASE_SENSITIVE_EQUAL,
          }).length > 0
        : false,
    [matches, searchValue],
  );

  return filterable || customFilterable ? (
    <AriakitComboboxProvider resetValueOnHide setValue={(v) => setSearchValue(v)}>
      <SelectProvider {...providerProps}>
        <Provider store={store} options={options}>
          <Trigger {...props} />
          <Popover style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <Box px="4" pb="4" display="flex" alignItems="center">
              <AriakitCombobox
                autoSelect
                placeholder="Search..."
                className={combobox}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              {resultsLoading && (
                <IconSolidLoading
                  className={`${loadingIcon} ${loadingIconPosition}`}
                  color={themeVars.static.content.weak}
                />
              )}
            </Box>

            <AriakitComboboxList>
              {searchValue && !hasExactMatch && creatable && (
                <AriakitComboboxItem
                  className={item}
                  value={searchValue}
                  onClick={() => {
                    handleCreateOption(searchValue);
                  }}
                >
                  {/* item check only needed for spacing */}
                  <ItemCheck />
                  <Text size="small" weight="medium" color="secondaryContentOnEnabled">
                    {searchValue}
                  </Text>
                </AriakitComboboxItem>
              )}
              {matches.map((option) => (
                <Option key={option.value} value={String(option.value)} render={<AriakitComboboxItem />}>
                  {option.name}
                </Option>
              ))}
            </AriakitComboboxList>
          </Popover>
        </Provider>
      </SelectProvider>
    </AriakitComboboxProvider>
  ) : (
    <SelectProvider {...providerProps}>
      <Provider store={store} options={options}>
        <Trigger {...props} />
        <Popover style={{ maxHeight: '300px', overflowY: 'auto' }}>
          {Array.isArray(optionsProp)
            ? options.map((option) => (
                <Option key={option.value} value={String(option.value)}>
                  {option.name}
                </Option>
              ))
            : children}
        </Popover>
      </Provider>
    </SelectProvider>
  );
};

const Trigger: React.FC<Omit<SelectProps, 'value' | 'setValue'>> = ({ renderValue, renderTag, trigger, ...props }) => {
  const Component = trigger ?? SelectTrigger;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const store = useAriakitSelectContext()!;
  const value = store.useState('value');
  const { displayMode, loading, options } = useSelectContext();

  if (loading) {
    props.disabled = true;
  }

  const optionsForItems = (opts: string | string[]) => {
    const items = store.getState().items;

    // If we have no options or items yet, use the passed in options.
    if (!options?.length && !items.length) {
      return Array.isArray(opts) ? opts.map(stringOrNumberToOption) : stringOrNumberToOption(opts);
    }

    // If no options are passed in via props, use the items registered by
    // rendering Option components.
    if (!options?.length) {
      const arrayOpts = Array.isArray(opts) ? opts : [opts];
      const matchingItems = items.filter((i) => arrayOpts.some((opt) => optionsMatch(i.value!, opt)));

      if (Array.isArray(opts)) {
        return itemsToOptions(matchingItems);
      } else {
        return itemToOption(matchingItems[0]);
      }
    }

    const findOption = (val: string | number): SelectOption => {
      const foundOption = (options ?? [])
        .map((option) =>
          typeof option === 'string'
            ? ({
                name: option,
                value: option,
              } as SelectOption)
            : option,
        )
        .find((option) => optionsMatch(option, val));

      return foundOption ?? stringOrNumberToOption(val);
    };

    return Array.isArray(opts) ? opts.map(findOption) : findOption(opts);
  };

  const renderSelectValue = (selectValue: AriakitSelectStoreState['value']) => {
    if (renderValue) {
      return renderValue(selectValue) ?? props.placeholder ?? 'Select...';
    }

    const selectedOptions = optionsForItems(selectValue);

    if (displayMode === 'tags') {
      if (!selectedOptions || (Array.isArray(selectedOptions) && !selectedOptions.length)) {
        return props.placeholder ?? 'Select...';
      }

      return (
        <Stack direction="row" align="center" gap="6" flexWrap="wrap" whiteSpace="normal" width="full" minWidth="none">
          {Array.isArray(selectedOptions) ? (
            selectedOptions.map((o) => (
              <SelectTag key={o.value} value={o.value}>
                {renderTag ? renderTag(o.name) : o.name}
              </SelectTag>
            ))
          ) : (
            <SelectTag value={selectedOptions.value}>{selectedOptions.name}</SelectTag>
          )}
        </Stack>
      );
    }

    if (Array.isArray(selectedOptions)) {
      return selectedOptions.length
        ? selectedOptions.map((o) => o.name).join(', ')
        : (props.placeholder ?? 'Select...');
    }

    return selectedOptions.name;
  };

  return (
    <Component
      style={{
        opacity: props.disabled ? 0.7 : 1,
        backgroundColor: props.disabled ? 'var(--lp-color-bg-interactive-disabled)' : undefined,
      }}
      {...props}
    >
      {value ? (
        renderSelectValue(value)
      ) : (
        <Text color="secondaryContentOnEnabled">{props.placeholder ?? 'Select...'}</Text>
      )}
    </Component>
  );
};

type ProviderProps = AriakitSelectProviderProps & {
  store: AriakitSelectStore;
  options?: SelectOption[] | undefined;
};
export const Provider: React.FC<ProviderProps> = ({ children, ...props }) => {
  const { value, setOptions, setValue } = useSelectContext();
  const items = props.store.useState('items');

  useEffect(() => {
    if (setOptions && !props.options && items.length) {
      // If we have no options passed, create them from the items which were
      // registered by the Option component.
      setOptions(itemsToOptions(items));
    }
  }, [items]);

  return (
    <AriakitSelectProvider value={anyOptionsToStringValue(value)} setValue={setValue} {...props}>
      {children}
    </AriakitSelectProvider>
  );
};

type SelectTriggerProps = AriakitSelectProps & {
  hideArrow?: boolean;
};
export const SelectTrigger: React.FC<SelectTriggerProps> = ({ children, hideArrow, ...props }) => {
  const { clearable, value, setValue } = useSelectContext();
  const isMulti = Array.isArray(value);
  const clearRef = useRef<HTMLButtonElement>(null);

  return (
    <AriakitSelect
      className={select}
      {...props}
      toggleOnPress={(e) => {
        if (clearRef.current?.contains(e.target as Node) || clearRef.current === e.target) {
          return false;
        }

        return true;
      }}
    >
      <Stack direction="row" align="center" justify="space-between" width="full" minWidth="none">
        {typeof children === 'string' ? (
          <Text color="secondaryContentOnEnabled" lines="1" align="left">
            {children}
          </Text>
        ) : (
          children
        )}

        <Stack direction="row" align="center" gap="6">
          {clearable && !props.disabled && (isMulti ? value.length > 0 : !!value) && (
            <ButtonIcon
              ref={clearRef}
              icon={<IconSolidXCircle />}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                Array.isArray(value)
                  ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    setValue!([])
                  : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    setValue!(undefined);
              }}
              size="minimal"
              emphasis="none"
              kind="secondary"
            />
          )}

          {!hideArrow && (
            <AriakitSelectArrow render={<IconSolidSelector color={themeVars.static.content.moderate} />} />
          )}
        </Stack>
      </Stack>
    </AriakitSelect>
  );
};

export const SelectTriggerButton: React.FC<SelectTriggerProps> = ({ children, ...props }) => (
  <AriakitSelect {...props} render={<Button />}>
    <Stack direction="row" gap="4" align="center">
      {children}
      <AriakitSelectArrow render={<IconSolidSelector />} />
    </Stack>
  </AriakitSelect>
);

type LableProps = AriakitSelectLabelProps;
export const Label: React.FC<LableProps> = ({ children, ...props }) => (
  <AriakitSelectLabel {...props}>{children}</AriakitSelectLabel>
);

// Could improve the types by allow value to receive a number as well as a
// string. Would probably need to do something with manually registering items
// in options because AriakitSelectItem only accepts a string value.
export type OptionProps = AriakitSelectItemProps;
export const Option: React.FC<OptionProps> = ({ children, value, ...props }) => {
  const valuesToMatch = [value];

  if (typeof children === 'string') {
    valuesToMatch.push(children);

    if (value === undefined) {
      value = children;
    }
  }

  const storeValue =
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    useAriakitSelectContext()!.useState('value');
  const selected = Array.isArray(storeValue)
    ? valuesToMatch.some((v) =>
        storeValue.includes(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          v!,
        ),
      )
    : valuesToMatch.includes(storeValue);

  return (
    <AriakitSelectItem focusOnHover value={value} className={item} {...props}>
      <ItemCheck checked={selected} style={{ flexShrink: 0 }} />
      {children ? (
        typeof children === 'string' ? (
          <Text size="small" weight="medium" color="secondaryContentOnEnabled" lines="1">
            {children}
          </Text>
        ) : (
          children
        )
      ) : (
        <Text size="small" weight="medium" color="secondaryContentOnEnabled" lines="1">
          {value}
        </Text>
      )}
    </AriakitSelectItem>
  );
};

export const ItemCheck: React.FC<AriakitSelectItemCheckProps> = ({ children, ...props }) => {
  const { checkType } = useSelectContext();

  if (checkType === 'checkbox') {
    return <Box cssClass={checkbox}>{props.checked && <IconSolidCheckCircle color="white" size="13" />}</Box>;
  }

  return (
    <AriakitSelectItemCheck {...props} className={checkmark}>
      {children ?? <IconSolidCheck size="16" />}
    </AriakitSelectItemCheck>
  );
};

type PopoverProps = AriakitSelectPopoverProps;
export const Popover: React.FC<PopoverProps> = ({ children, ...props }) => (
  <AriakitSelectPopover gutter={4} className={popover} {...props}>
    {/*
			There is a bug in Ariakit where you need to have this arrow rendered or
			else positioning of the popover breaks. We render it, but hide it by
			setting size={0}. This is an issue with anything using a popover coming
			from the floating-ui library.
			*/}
    <AriakitPopoverArrow size={0} />

    {children}
  </AriakitSelectPopover>
);

export const Group: React.FC<React.PropsWithChildren> = ({ children }) => (
  <AriakitSelectGroup>{children}</AriakitSelectGroup>
);

type GroupLabelProps = AriakitSelectGroupLabelProps;
export const GroupLabel: React.FC<GroupLabelProps> = ({ children, ...props }) => (
  <AriakitSelectGroupLabel {...props}>{children}</AriakitSelectGroupLabel>
);

const isOption = (value: SingleValue): value is SelectOption => value !== null && typeof value === 'object';

const singleValueToOption = (value: SingleValue) => {
  if (isOption(value)) {
    return value;
  }

  return {
    name: String(value),
    value: String(value),
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const valueToOptions = (value: any | undefined) => {
  if (value === undefined) {
    return;
  }

  if (Array.isArray(value)) {
    return value.map(singleValueToOption);
  }

  return singleValueToOption(value);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const optionToStringValue = (option: any | undefined) => {
  if (option === undefined) {
    return '';
  }

  if (isOption(option)) {
    return String(option.value);
  } else {
    return String(option);
  }
};

const itemsToOptions = (items: AriakitSelectStoreState['items']) => items.map(itemToOption);

const itemToOption = (itm: AriakitSelectStoreState['items'][0]): SelectOption => ({
  name: String(itm.element?.innerText ?? itm.value),
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  value: itm.value!,
});

const stringOrNumberToOption = (value: string | number): SelectOption => ({
  name: String(value),
  value,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const anyOptionsToStringValue = (options: any[] | any | undefined) => {
  if (Array.isArray(options)) {
    return options.map(optionToStringValue);
  }

  return optionToStringValue(options);
};

const optionsMatch = (
  option: string | number | SelectOption,
  searchValue: string | number | SelectOption | undefined,
): boolean => {
  if (searchValue === undefined) {
    return false;
  }

  if (isOption(option) && isOption(searchValue)) {
    const optionValues = [option.value, option.name].map(String);
    const searchValues = [searchValue.value, searchValue.name].map(String);
    return optionValues.some((v) => searchValues.includes(v));
  } else if (isOption(option)) {
    const optionValues = [option.value, option.name].map(String);
    return optionValues.includes(String(searchValue));
  } else if (isOption(searchValue)) {
    const searchValues = [searchValue.value, searchValue.name].map(String);
    return searchValues.includes(String(option));
  }

  return String(option) === String(searchValue);
};

const SelectTag: React.FC<{ children: string; value: string | number }> = ({ children, value }) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const selectStore = useAriakitSelectContext()!;
  const selectValue = selectStore.useState('value');
  const name = children;

  return (
    <Badge
      cursor="pointer"
      shape="basic"
      variant="white"
      size="medium"
      label={name}
      lines="1"
      maxWidth="full"
      overflow="hidden"
      minWidth="none"
      iconEnd={<IconSolidX />}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();

        const newValue = Array.isArray(selectValue) ? selectValue.filter((v) => !optionsMatch(v, { name, value })) : '';

        selectStore.setValue(newValue);
      }}
    />
  );
};

type SelectComponentType = typeof SelectInternal & {
  Label: typeof Label;
  Group: typeof Group;
  GroupLabel: typeof GroupLabel;
  Option: typeof Option;
  Popover: typeof Popover;
  SelectTriggerButton: typeof SelectTriggerButton;
  useContext: typeof useAriakitSelectContext;
  useStore: typeof useAriakitSelectStore;
};

export const Select = SelectInternal as SelectComponentType;

Select.Label = Label;
Select.Group = Group;
Select.GroupLabel = GroupLabel;
Select.Option = Option;
Select.Popover = Popover;
Select.SelectTriggerButton = SelectTriggerButton;
Select.useContext = useAriakitSelectContext;
Select.useStore = useAriakitSelectStore;
